import { useCallback, useMemo, useRef } from "react";
import { JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Notification, Pagination, Popover, Table, toaster, Whisper } from "rsuite";
import { useInactiveInventoryData, usePotentialOverstockData, useStockOutData } from "../../services/redux/hooks/useInventory";
import { INACTIVE_INV_TABLE, OVERSTOCK_INV_TABLE, PO_INV_TABLE, STOCKOUT_INV_TABLE } from "../../utils/tableheaders";
import { formatNumber, splitParts, convertVhToPx } from "../../utils/formatter";
import { CSVLink } from "react-csv";
import moment from "moment";
import { IRoot } from "../../services/interface/IHome";
import RebalancingTable from "./rebalancingDataTable";
import { IAlertRow } from "../../services/interface/IForecast";
import { setApplyFilter, setMaterial, setPlant, setSelectedClusterName, setSelectionFlag } from "../../services/redux/reducer/filter.reducer";

const { Column, Cell, HeaderCell } = Table;

const InventoryTable = ({tableHeight}:any) => {
    const dispatch = useDispatch();
    const exportFullRef = useRef<any>();
    const [loading, setLoading] = useState(false);
    const [sortColumn, setSortColumn] = useState('');
    const [sortType, setSortType] = useState();
    const [selectedFilterData, setSelectedFilterData] = useState({
        isExport: false,
        pageNumber: 1,
        pageSize: 100,
        monthNumber: [0, 1],
    });
    const [total, setTotal] = useState<number>(0);
    const [gbpfilter, setGbpFilter] = useState<any>({
        plant: [],
        l2: [],
        l3: [],
        materialDescription: [],
        material: [],
        mrpType: [],
        futureSegmentation: [],
        vendorName: [],
        clustername: [],
        alertType: 0,
        alerts: [],
        reviewedFlag: [],
        poAlert: [],
        adjustedLogic: []
    });
    const [tableHeader, setTableHeader] = useState<{ 'label': string, 'key': string }[]>([]);
    const [tableData, setTableData] = useState([]);
    const [monthNumber, setMonthNumber] = useState<number[]>([0, 1]);
    const windowSize = useSelector((state: IRoot) => state.filter.windowSize);
    const selectionFlag = useSelector((state:IRoot)=>state.filter.selectionFlag);
    const selectedGBPFilter = useSelector((state: IRoot) => state.filter.selectedGBPFilter);
    const alertType = useMemo(() => new URLSearchParams(location.search).get("alert"), [location.search]);
    const [selectedSKUL, setSelectedSKUL] = useState<string[]|number[]|null>();
    const [isOpenModal, setOpenModal] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<any[]>([]);
    const [selectedRowData, setSelectedRowData] = useState<IAlertRow[]>([]);
    const applyFilter = useSelector((state:IRoot)=>state.filter.applyFilter);

    const handleSortColumn = (sortColumn: any, sortType: any) => {
        setSortColumn(sortColumn);
        setSortType(sortType);
    };

    const naturalSort = (a: { [x: string]: string; }, b: { [x: string]: string; }) => {
        const aParts = splitParts(a[sortColumn]);
        const bParts = splitParts(b[sortColumn]);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i];
            const bPart = bParts[i];

            if (aPart !== bPart) {
                if (typeof aPart === 'number' && typeof bPart === 'number') {
                    return aPart - bPart;
                }
                return aPart > bPart ? 1 : -1;
            }
        }
        return 0;
    };

    const getSortedData = () => {
        if (sortColumn && sortType) {
            return [...tableData].sort((a, b) => {
                const result = naturalSort(a, b);
                return sortType === 'asc' ? result : -result;
            });
        }
        return tableData;
    };

    const sortedData = getSortedData();
    const { mutate: mutatePOTable, isSuccess, data: poData } = usePotentialOverstockData();
    const { mutate: mutatePStockOutTable, data: potentialStockOutData } = useStockOutData();
    const { mutate: mutateAgeingInactive, isSuccess: isAgeingSuccess, data: ageingInventoryData } = useInactiveInventoryData();

    const handleRowSelection = (data: any) => {
        let index = selectedRow.indexOf(data?.SKUl);
        if (index !== -1) {
            setSelectedRow([]);
            setSelectedRowData([]);
            dispatch(setPlant([]));
            dispatch(setMaterial([]));
            dispatch(setSelectedClusterName([]));
            dispatch(setSelectionFlag(false));
            dispatch(setApplyFilter({"status" : true, "rowNo" : index}));
        } else {
            setSelectedRow([data?.SKUl]);
            setSelectedRowData([data]);
            dispatch(setPlant([data?.plant]));
            dispatch(setMaterial([data?.material]));
            dispatch(setSelectedClusterName([data?.cluster]));
            dispatch(setSelectionFlag(true));
            dispatch(setApplyFilter({"status" : true, "rowNo" : index}));
        }
    }

    const paginationHandler = (data: any) => {
        let payload = {
            ...gbpfilter,
            ...selectedFilterData,
            pageNumber: data?.pageNumber ? data?.pageNumber : 1,
            pageSize: 100,
        }
        delete payload.adjustedLogic;
        delete payload.alerts;
        delete payload.poAlert;
        delete payload.reviewedFlag;
        if (alertType === "PotentialOverstock")
            mutatePOTable(payload);
        else if (alertType === "PotentialStockout") {
            payload = {
                ...payload,
                alertType: 1,
            }
            mutatePStockOutTable(payload);
        }
        else if (alertType === "AgeingInventory") {
            payload = {
                ...payload,
                alertType: 2
            }
            mutateAgeingInactive(payload);
        }
    }

    const reportDownloadHandler = () => {
        toaster.push(message, { placement: "topCenter" });
    }

    const createPotentialOverstockPayload = useCallback(() => {
        const payload = {
            ...gbpfilter,
            ...selectedGBPFilter,
            alerts: [],
            isExport: false,
            pageNumber: 1,
            pageSize: 100
        };
        delete payload.alertType;
        return payload;
    }, [gbpfilter, selectedGBPFilter]);

    const createPotentialStockoutPayload = useCallback(() => {
        const payload = {
            ...gbpfilter,
            ...selectedGBPFilter,
            alertType: 1,
            alerts: [],
            isExport: false,
            pageNumber: 1,
            pageSize: 100
        };
        delete payload.adjustedLogic;
        delete payload.alerts;
        delete payload.poAlert;
        delete payload.reviewedFlag;
        return payload;
    }, [gbpfilter, selectedGBPFilter]);

    const createAgeingInventoryPayload = useCallback(() => {
        const payload = {
            ...gbpfilter,
            ...selectedGBPFilter,
            alertType: 2,
            alerts: [],
            isExport: false,
            pageNumber: 1,
            pageSize: 100
        };
        delete payload.adjustedLogic;
        delete payload.alerts;
        delete payload.poAlert;
        delete payload.reviewedFlag;
        return payload;
    }, [gbpfilter, selectedGBPFilter]);

    useEffect(() => {
        if (alertType === "PotentialOverstock") {
            setTableHeader(OVERSTOCK_INV_TABLE);
        }
        else if (alertType === "PotentialStockout") {
            setTableHeader(STOCKOUT_INV_TABLE);
        }
        else if (alertType === "AgeingInventory" || alertType === "DevaluedInventory") {
            setTableHeader(INACTIVE_INV_TABLE);
        }
    }, [alertType]);

    useEffect(() => {
        if(applyFilter.status || applyFilter.rowNo===-1){
            if(!selectionFlag){
                if (alertType === "PotentialOverstock") {
                    const payload = createPotentialOverstockPayload();
                    mutatePOTable(payload);
                } else if (alertType === "PotentialStockout") {
                    const payload = createPotentialStockoutPayload();
                    mutatePStockOutTable(payload);
                } else if (alertType === "AgeingInventory" || alertType === "DevaluedInventory") {
                    const payload = createAgeingInventoryPayload();
                    mutateAgeingInactive(payload);
                }
            }
        }
    }, [alertType, applyFilter]);

    useEffect(() => {
        if (alertType === "PotentialOverstock") {
            setLoading(true);
            if (poData) {
                setTableData(poData?.data);
                setTotal(poData?.count);
            }
            if (isSuccess) {
                setLoading(false);
            }
        }
    }, [poData]);

    useEffect(() => {
        if (alertType === "PotentialStockout") {
            setLoading(true);
            if (potentialStockOutData) {
                setTableData(potentialStockOutData?.data);
                setTotal(potentialStockOutData?.count);
            }
            setLoading(false);
        }
    }, [potentialStockOutData]);

    useEffect(() => {
        if (alertType === "AgeingInventory" || alertType === "DevaluedInventory") {
            setLoading(true);
            if (ageingInventoryData) {
                setTableData(ageingInventoryData?.data);
                setTotal(ageingInventoryData?.count);
            }
            if (isAgeingSuccess) {
                setLoading(false);
            }
        }
    }, [ageingInventoryData]);

    useEffect(()=>{
        if(applyFilter.status === false){
            setSelectedRow([]);
            setSelectedRowData([]);
            dispatch(setPlant([]));
            dispatch(setMaterial([]));
            dispatch(setSelectedClusterName([]));
            dispatch(setSelectionFlag(false));
        }   
    },[applyFilter.status]);

    const CustomeCell = ({ rowData, dataKey, ...props }: any) => {
        const handleRowSelection=(data:any)=>{
            props?.handleRowSelection({
                SKUl:data?.material + "_" + data.plant,
                plant:data?.plant,
                material:data?.material,
                cluster:data?.cluster
            })
        }
        return (
            <Cell {...props}
                onClick={(e) => {
                    if ((e.target instanceof HTMLInputElement)) {
                        return;
                    }
                    handleRowSelection(rowData)
                }}>
                {(alertType === "AgeingInventory" || alertType === "DevaluedInventory") && dataKey === "material" ? (
                    <p className="table-cell-title font-normal flex items-start gap-2">
                        <span>{rowData[dataKey]}</span>
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                props.setopenmodal([rowData?.material, rowData?.plant ,rowData?.cluster]);
                            }}
                            className="mt-[2px] cursor-pointer"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M6 12.5C9.31371 12.5 12 9.81371 12 6.5C12 3.18629 9.31371 0.5 6 0.5C2.68629 0.5 0 3.18629 0 6.5C0 9.81371 2.68629 12.5 6 12.5ZM6 2.90234C5.45964 2.90234 5.18945 3.14486 5.18945 3.62988C5.18945 4.11816 5.45964 4.3623 6 4.3623C6.27018 4.3623 6.47201 4.29883 6.60547 4.17188C6.74219 4.04167 6.81055 3.861 6.81055 3.62988C6.81055 3.14486 6.54036 2.90234 6 2.90234ZM5.25293 10.5H6.74219V5.04102H5.25293V10.5Z"
                                    fill="#046A38"
                                />
                            </svg>
                        </span>
                    </p>
                ) : ["povalue","onHand$","Onhand$Value", "Onhand$Value_DevaluedBy"].includes(dataKey) ? (
                    <p className="table-cell-title font-normal">${formatNumber(rowData[dataKey])}</p>
                ) : dataKey === "Lst_Rc_Dt" ? (
                    <p className="table-cell-title font-normal">{`${moment(String(rowData[dataKey]).slice(0, 10)).format("YYYY-MM-DD")}`}</p>
                ) : dataKey === "Adjusted_ORQ" ? (
                    <p className="table-cell-title font-normal">{formatNumber(rowData[dataKey])}</p>
                ) : ["Effective_SS", "InvAtEndOfLT1", "InvAtEndOfLT2", "Total_Dmd_Within_LT_Custom","poqty", 
                    "open_eros", "onhand", "onHand", "InvAtEndOfLT", "Qty_DevaluedBy"].includes(dataKey) ? (
                    <p className="table-cell-title font-normal">{formatNumber(rowData[dataKey])}</p>
                ) : (
                   <Whisper
                        placement="auto"
                        trigger={['hover', 'focus']}
                        container={() => document.body}
                        speaker={(sp, ref) => (
                            <Popover
                            ref={ref}
                            {...sp}
                            style={{ maxWidth: 280, width: 'max-content' }}
                            >
                            <div className="break-words">
                                {String(rowData[dataKey] ?? '')}
                            </div>
                            </Popover>
                        )}
                        >
                        <p className="table-cell-title font-normal max-w-xs whitespace-nowrap overflow-hidden text-ellipsis">
                            {String(rowData[dataKey] ?? '').length > 12
                            ? `${String(rowData[dataKey] ?? '').slice(0, 12)}…`
                            : String(rowData[dataKey] ?? '')}
                        </p>
                        </Whisper>
                )}
            </Cell>
        );
    };
    
    return <>
        <div>
            <Table
                height={convertVhToPx(tableHeight)}
                loading={loading}
                bordered
                data={sortedData}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                headerHeight={70}
                shouldUpdateScroll={false}
                cellBordered
                className="table-container"
                rowClassName={(rowData, rowIndex) => {
                    if(rowData){
                        let className = rowIndex % 2 === 0 ? 'db-tablebg' : '';
                        const key = rowData["material"] + "_" + rowData["plant"];
                        if (rowData!==undefined && selectedRow.includes(key || "")) {
                          className += ' rs-row-selection';
                        }
                        else if(selectedRow.length > 0){
                            className  += ' rs-row-nonselection'
                        }
                        return className;
                    }
                    return "";
                }}
            >
                {tableHeader?.map((value: { key: Key | null | undefined; label: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }, index: number) => {
                const width = value.key === 'material' ? 150 : 
                  index === 0 ? 95 : 
                  index === tableHeader.length - 1 ? 90 : 135;

                    return <Column key={value?.key} flexGrow={1} align="left" minWidth={width} sortable>
                        <HeaderCell className="font-semibold table-header-title" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <div className="header-content font-semibold table-header-title">
                                {value.label}
                            </div>
                        </HeaderCell>
                        <CustomeCell 
                        setopenmodal={(data:any)=>{
                            
                            setOpenModal(true);
                            setSelectedSKUL(data)
                            
                        }}
                        handleRowSelection={(data:any)=>{
                            handleRowSelection(data);
                        }}
                        // ageingGraphHandler={(data:any)=>{
                        //     ageingGraphHandler(data);
                        // }}
                        dataKey={value.key} 
                        />
                    </Column>
                })}
            </Table>
        </div>
        <div className="flex grow-0 p-[5px] items-center justify-between">
            <div className="w-[85%] flex justify-center">
                <Pagination
                    layout={['pager', 'skip']}
                    prev
                    next
                    first
                    last
                    total={total}
                    limit={selectedFilterData?.pageSize}
                    size={"sm"}
                    activePage={selectedFilterData?.pageNumber}
                    onChangePage={(page: number) => {
                        setSelectedFilterData({
                            ...selectedFilterData,
                            pageNumber: page,
                            monthNumber: monthNumber
                        })
                        paginationHandler({
                            pageNumber: page
                        })
                    }}
                />
            </div>
            <div className="w-[15%] flex justify-evenly 2xl:gap-1">
                <Button onClick={reportDownloadHandler} className="bg-deloitteGreen text-white px-[20px] h-[30px] rounded font-semibold btn-text">
                    <CSVLink
                        data={tableData}
                        ref={exportFullRef}
                        headers={tableHeader}
                        filename={String(alertType)}
                    >
                        Export
                    </CSVLink>
                </Button>
            </div>
        </div>
        <Modal size={'50vw'} open={isOpenModal} className="border-[1px] rounded-md"
            onClose={() => {
                setOpenModal(false); setSelectedSKUL(null)
            }}>
            <Modal.Body>
                <div className="text-center mb-2">
                    <p className="text-fs20 text-black font-semibold">Inventory Details to explore Rebalancing Opportunities</p>
                </div>
                <div className="flex mb-2">
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw] font-bold">Item ID</div>
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw]">{`${selectedSKUL?.[0]}`}</div>
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw] font-bold">DC</div>
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw]">{`${selectedSKUL?.[1]}`}</div>
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw] font-bold">District</div>
                    <div className="text-fs16 flex p-[1%] border border-[#BEBEBE] w-[6vw]">{`${selectedSKUL?.[2]}`}</div>
                </div>
                <RebalancingTable isHeightHidden={false} SKUl={selectedSKUL?.[0]} />
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => { setOpenModal(false); setSelectedSKUL(null) }} className="bg-[#949494] 
                        text-fs16 font-semibold text-white focus:bg-[#949494]" appearance="subtle">
                    Close
                </Button>

            </Modal.Footer>
        </Modal>
    </>
}

const message = (
    <Notification type={"info"} header={`Exporting...`} closable>
        <p>Your file is being created. It will be download automatically</p>
    </Notification>
);

export default InventoryTable;